<template>
    <div class="SbsAudioPlayer">
        <AudioProgressbar
            :disabled="isLoading"
            :current="currentTime"
            :duration="duration"
            :episodes="episodes"
            @seekTo="handleSeekTo" />

        <div class="SbsAudioPlayer__Box">
            <AudioInfo
                :trackTitle="title"
                :trackCover="cover"
                :duration="durationLeft"
                :episodeTitle="episodeTitle" />

            <AudioPlayControls
                class="SbsAudioPlayer__PlayControls"
                :isPlaying="isPlaying"
                :seekStep="seekStep"
                :disabled="isLoading"
                @togglePlay="togglePlay"
                @seekForward="handleSeekForward"
                @seekBackward="handleSeekBackward" />

            <div class="SbsAudioPlayer__Actions">
                <AudioVolumeControls
                    v-model="volume"
                    :isMuted="isMuted"
                    :disabled="isLoading"
                    @mute="handleMute" />

                <AudioPlaybackRateControls
                    class="SbsAudioPlayer__PlaybackRate"
                    :playbackRate="playbackRate"
                    :disabled="isLoading"
                    @changePlaybackRate="handlePlaybackRate" />

                <AudioCloseBtn class="SbsAudioPlayer__CloseBtn" @close-player="handleClose"/>
            </div>
        </div>
    </div>
</template>

<script>
import { Howl } from 'howler';
import AudioInfo from './components/AudioInfo.vue';
import AudioCloseBtn from './components/AudioCloseBtn.vue';
import AudioProgressbar from './components/AudioProgressbar.vue';
import AudioVolumeControls from './components/AudioVolumeControls.vue';
import AudioPlayControls from './components/AudioPlayControls.vue';
import AudioPlaybackRateControls from './components/AudioPlaybackRateControls.vue';

const DEFAULT_VOLUME = 0.3;

export default {
    name: 'SbsAudioPlayer',
    components: {
        AudioInfo,
        AudioCloseBtn,
        AudioProgressbar,
        AudioPlayControls,
        AudioVolumeControls,
        AudioPlaybackRateControls,
    },
    emits: ['close', 'play', 'pause', 'stop', 'volume', 'progress', 'seek'],
    data() {
        return {
            sound: null,
            isPlaying: false,
            isLoading: false,
            isMuted: false,
            volume: DEFAULT_VOLUME,
            savedVolume: DEFAULT_VOLUME,
            playbackRate: 1,
            duration: 0,
            currentTime: 0,
        };
    },
    computed: {
        durationLeft() {
            return this.duration - this.currentTime;
        },
        episode() {
            return this.episodes.filter(episode => this.currentTime >= episode.time).pop();
        },
        episodeTitle() {
            if (this.episode) {
                return this.episode.text;
            }

            return '';
        },
    },
    methods: {
        togglePlay() {
            const newPlayState = !this.sound?.playing();

            this.changePlayState(newPlayState);
        },
        changePlayState(playing) {
            if (playing) {
                if (!this.sound?.playing()) {
                    this.sound?.play();
                }
            } else if (this.sound?.playing()) {
                this.sound?.pause();
            }
        },
        handleSeekBackward() {
            const seekBackward = Math.max(0, this.sound.seek() - this.seekStep);

            this.seekTo(seekBackward);
        },
        handleSeekForward() {
            const seekForward = Math.min(this.sound.duration(), this.sound.seek() + this.seekStep);

            this.seekTo(seekForward);
        },
        handleSeekTo(val) {
            this.seekTo(val);
            this.$emit('seek', val);
        },
        handleClose() {
            this.$emit('close');
        },
        handleMute() {
            if (this.isMuted) {
                this.isMuted = false;
                this.volume = this.savedVolume || DEFAULT_VOLUME;
            } else {
                this.isMuted = true;
                this.savedVolume = this.volume;
                this.volume = 0;
            }
        },
        handlePlaybackRate(val) {
            this.playbackRate = val;
        },
        handleLoad() {
            this.duration = this.sound?.duration() ?? 0;
            this.isLoading = false;
        },
        handlePlay() {
            this.isPlaying = true;
            requestAnimationFrame(() => this.runStep());
            this.$emit('play');
        },
        handlePause() {
            this.isPlaying = false;
            this.$emit('pause');
        },
        handleSeek() {
            clearTimeout(this.idRunStep);
            this.runStep();
            // состояния плеера не успевает обновится
            this.idRunStep = setTimeout(() => this.runStep(), 100);

            this.$emit('seek', this.getSeek());
        },
        getSeek() {
            return this.sound?.seek() ?? 0;
        },
        runStep() {
            cancelAnimationFrame(this.idStepFrame);
            this.currentTime = this.getSeek();

            if (this.sound?.playing()) {
                this.idStepFrame = requestAnimationFrame(() => this.runStep());
            }
        },
        handleEndStop() {
            this.isPlaying = false;
            this.$emit('stop');
        },
        seekTo(val) {
            this.sound?.seek(val);
        },
        initPlayer() {
            this.sound?.unload();

            this.isLoading = true;
            this.sound = new Howl({
                src: this.sources,
                rate: this.playbackRate,
                html5: true,
                preload: true,
                loop: false,
                volume: this.volume,
                autoplay: this.playing,
                onload: this.handleLoad,
                onplay: this.handlePlay,
                onpause: this.handlePause,
                onseek: this.handleSeek,
                onend: this.handleEndStop,
                onstop: this.handleEndStop,
            });

            this.sound.once('load', () => {
                this.sound.seek(this.startTime);
            });
        },
    },
    props: {
        /**
         * Обложка для аудио
         */
        cover: {
            type: String,
            default: '',
        },
        /**
         * титул для аудио
         */
        title: {
            type: String,
            default: '',
        },
        /**
         * Путь до аудио.
         * можно добавлять разные форматы одного файла, Они должны быть в порядке предпочтения, плеер автоматически загружать первый, совместимый с текущим браузером.
         */
        sources: {
            type: Array,
            default: () => ([]),
        },
        /**
         * Массив эпизодов,
         * значения "time" в минутах
         */
        episodes: {
            type: Array,
            default: () => ([]),
        },
        /**
         * инициализировать время воспроизведения в секундах
         */
        startTime: {
            type: Number,
            default: 0,
        },
        /**
         * шаг вперед и назад, в секундах
         */
        seekStep: {
            type: Number,
            default: 15,
        },
        /**
         * управление воспроизведением
         */
        playing: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        volume(newVal) {
            this.sound?.volume(newVal);
            this.isMuted = Number(newVal) === 0;
            this.$emit('volume', newVal);
        },
        isMuted(newVal) {
            this.sound?.mute(newVal);
        },
        playbackRate(newVal) {
            this.sound?.rate(newVal);
        },
        sources(newSources, oldSources) {
            if (String(newSources) !== String(oldSources)) {
                this.initPlayer();
            }
        },
        startTime(val) {
            this.seekTo(val);
        },
        currentTime(val) {
            this.$emit('progress', val);
        },
        playing(val) {
            this.changePlayState(val);
        },
    },
    mounted() {
        this.initPlayer();
    },
    beforeUnmount() {
        this.sound?.unload();
    },
};
</script>

<style lang="scss" scoped>
.SbsAudioPlayer {
    width: 100%;
}

.SbsAudioPlayer__Box {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    align-items: center;
    justify-content: space-between;
    height: 90px;
    padding: 12px 24px;
    background-color: $white;
}

.SbsAudioPlayer__PlayControls {
    justify-self: center;
}

.SbsAudioPlayer__Actions {
    display: flex;
    align-items: center;
    justify-self: end;
}

.SbsAudioPlayer__PlaybackRate {
    width: 25px;
    margin-left: 20px;
}

.SbsAudioPlayer__CloseBtn {
    margin-left: 48px;
}
</style>
